import React, { useMemo } from 'react';
import { Icon } from '../../icons';

/**
 * SOPChecklist Component (V2.4 - Advanced Refinement Edition)
 * Optimized for high-density display, metadata completeness, and clear status tracking.
 * Bypass Bootstrap conflicts and maximize space efficiency.
 */

const SOPChecklist = ({ scenarioData, onCheckItem }) => {
  // 0: Pending (Red), 1: In Progress (Orange), 2: Completed (Indigo)
  const getStatusConfig = (status) => {
    switch (status) {
      case 1:
        return {
          bg: 'bg-orange-500/10',
          borderColor: 'rgba(249, 115, 22, 0.4)',
          badge: 'bg-orange-600 text-white',
          dot: 'bg-orange-500',
          infoBg: 'bg-orange-500/5',
          infoText: 'text-orange-400',
          infoLabel: '진행',
          label: '처리 진행중',
        };
      case 2:
        return {
          bg: 'bg-indigo-500/10',
          borderColor: 'rgba(99, 102, 241, 0.4)',
          badge: 'bg-indigo-600 text-white',
          dot: 'bg-indigo-500',
          infoBg: 'bg-indigo-500/5',
          infoText: 'text-indigo-400',
          infoLabel: '완료',
          label: '조치 완료',
        };
      default:
        return {
          bg: 'bg-red-500/10',
          borderColor: 'rgba(239, 68, 68, 0.4)',
          badge: 'bg-red-600 text-white',
          dot: 'bg-red-500',
          label: '미처리',
        };
    }
  };

  const progressCount = useMemo(() => {
    if (!scenarioData) return 0;
    return scenarioData.checklist.filter(i => i.is_checked === 2).length;
  }, [scenarioData]);

  const isAllResolved = useMemo(() => {
    if (!scenarioData || scenarioData.checklist.length === 0) return false;
    return progressCount === scenarioData.checklist.length;
  }, [scenarioData, progressCount]);

  // Last resolution time
  const resolvedAt = useMemo(() => {
    if (!isAllResolved) return null;
    const items = scenarioData.checklist.filter(i => i.is_checked === 2);
    if (items.length === 0) return null;
    const times = items.map(i => new Date(i.checked_at).getTime());
    return new Date(Math.max(...times));
  }, [scenarioData, isAllResolved]);

  if (!scenarioData) return null;

  return (
    <div className="fixed top-0 left-[80px] w-[380px] h-full z-[1400] flex flex-col pointer-events-none will-change-transform transform-gpu font-['Pretendard']">
      <div className="flex-1 flex flex-col bg-slate-950/95 backdrop-blur-[6px] border-r border-slate-800 shadow-[20px_0_50px_rgba(0,0,0,0.6)] pointer-events-auto animate-sop-entry overflow-hidden">

        {/* Header Section */}
        <div className="px-4 py-2 bg-slate-900 border-b border-white/5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_10px_#ef4444]" />
              <span className="text-[11px] font-black tracking-widest text-red-500">재난 대응 SOP</span>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-[10px] font-mono text-slate-500 font-bold uppercase tracking-tighter">LOG #{scenarioData.log_id}</span>
              <span className="text-[9px] font-bold text-slate-600 tracking-tight">발생: {new Date(scenarioData.start_time).toLocaleTimeString([], { hour12: false })}</span>
            </div>
          </div>

          <h2 className="text-xl font-black text-white flex items-center gap-2 tracking-tight">
            <Icon name="fireIcon" style={{ width: 24, height: 24, color: '#ef4444' }} />
            화재 발생 상황
          </h2>
        </div>

        {/* Scrollable Checklist */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 sop-scrollbar">
          {scenarioData.checklist.map((item) => {
            const config = getStatusConfig(item.is_checked);
            const isDone = item.is_checked === 2;
            const isInProgress = item.is_checked === 1;

            return (
              <div
                key={item.step_id}
                onClick={() => onCheckItem(item.step_id, (item.is_checked + 1) % 3)}
                className={`relative px-4 py-2.5 rounded-xl ${config.bg} cursor-pointer transition-all duration-200 hover:bg-slate-900 flex flex-col gap-1 group overflow-hidden shadow-inner`}
                style={{ border: '1px solid ' + config.borderColor }}
              >
                {/* Left Accent Bar */}
                <div className={`absolute left-0 top-0 bottom-0 w-1 ${config.dot}`} />

                {/* Top Row: Label & Checkbox */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded shadow-sm ${config.badge}`}>
                      {config.label}
                    </span>
                    <span className="text-[12px] font-black text-white/90 tracking-tighter">
                      STEP {item.index}
                    </span>
                  </div>

                  {/* Checkbox */}
                  <div
                    className={`w-5 h-5 rounded transition-colors flex items-center justify-center`}
                    style={{
                      border: isDone ? '1px solid transparent' : '1px solid rgba(255, 255, 255, 0.2)',
                      backgroundColor: isDone ? 'rgb(99, 102, 241)' : 'rgba(0, 0, 0, 0.2)',
                      boxShadow: isDone ? '0 0 8px rgba(99, 102, 241, 0.5)' : 'none'
                    }}
                  >
                    {isDone && (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={5} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>

                {/* Content */}
                <h3 className={`text-[14px] font-bold leading-snug tracking-tight ${isDone ? 'text-slate-500 line-through decoration-slate-600' : 'text-slate-100'}`}>
                  {item.response}
                </h3>

                {/* Metadata Row: Dense & Integrated */}
                <div className="flex items-center gap-x-2.5 text-[11px] font-bold text-slate-400">
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600 italic">담당:</span>
                    <span className="text-slate-200">{item.manager}</span>
                  </div>
                  <div className="w-px h-2 bg-white/10" />
                  <div className="flex items-center gap-1">
                    <span className="text-slate-600 italic">책임:</span>
                    <span className="text-slate-200">{item.supervisor || '-'}</span>
                  </div>
                  <div className="w-px h-2 bg-white/10" />
                  <div className="flex items-center gap-1 truncate">
                    <span className="text-slate-600 italic">부서:</span>
                    <span className="text-slate-200">{item.coop_department}</span>
                  </div>
                </div>

                {/* Status Timestamp Info Row (Redesigned) */}
                {(isInProgress || isDone) && (
                  <div className={`mt-0.5 py-1 px-2 rounded flex items-center justify-between ${config.infoBg}`}>
                    <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-tight">
                      <span className={config.infoText}>{config.infoLabel} 정보:</span>
                      <span className="text-slate-300">{item.checked_by || (isInProgress ? '대기중' : item.manager)}</span>
                    </div>
                    <span className={`text-[10px] font-mono ${config.infoText} font-bold`}>
                      {new Date(item.checked_at).toLocaleTimeString([], { hour12: false })}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer Section: Unified Progress & Time */}
        <div className="px-3 pt-1.5 bg-slate-900 border-t border-white/5 shrink-0">
          <div className="flex flex-col gap-2.5">
            <div className="flex justify-between text-[11px] font-black tracking-wider">
              <span className="text-slate-400">전체 대응 현황</span>
              <span className="text-indigo-400">{progressCount} / {scenarioData.checklist.length} <span className="text-[9px] text-slate-600 ml-0.5">STEPS</span> ({Math.round((progressCount / scenarioData.checklist.length) * 100)}%)</span>
            </div>

            <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.5)] transition-all duration-700 ease-out"
                style={{ width: `${(progressCount / scenarioData.checklist.length) * 100}%` }}
              />
            </div>

            <div className="flex justify-center border-t border-white/5 pt-1.5 w-full">
              {isAllResolved ? (
                <div className="flex items-center justify-center gap-2">
                  <p className="text-[10px] text-indigo-400 font-extrabold tracking-tight uppercase whitespace-nowrap">
                    상황 종료 시각: {resolvedAt ? resolvedAt.toLocaleTimeString([], { hour12: false }) : '-'}
                  </p>
                </div>
              ) : (
                <p className="text-[10px] text-slate-500 font-bold tracking-tight uppercase text-center">
                  대응 진행 중...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes sop-entry {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-sop-entry {
          animation: sop-entry 0.35s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .sop-scrollbar {
          scrollbar-width: thin !important;
          scrollbar-color: rgba(255, 255, 255, 0.2) rgba(0, 0, 0, 0.3) !important;
        }
        .sop-scrollbar::-webkit-scrollbar {
          width: 6px !important;
          display: block !important;
        }
        .sop-scrollbar::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.3) !important;
          border-radius: 10px !important;
        }
        .sop-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2) !important;
          border-radius: 10px !important;
          border: 1px solid rgba(255, 255, 255, 0.05) !important;
        }
        .sop-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.4) !important;
        }
      `}} />
    </div>
  );
};

export default React.memo(SOPChecklist);
